import React, { useState, useCallback, useContext } from 'react'
import { EventActionsContext } from './EventActionsContext'

import isEmpty from 'lodash/isEmpty'
import TextWrapper from './TextWrapper'
import PopOver from './PopOver'
import { Col, Button, Row } from 'reactstrap'
import { getFrench } from './FrenchTranslation'
import FormModal from './EventForm'
import { makeDeleteRequest } from 'utils/api'
import { Avatar, Box, Stack, Tooltip } from '@mui/material'
import AttachmentIcon from '@mui/icons-material/Attachment'
import { useSelector } from 'react-redux'
import moment from 'moment'
import { convertToUserDateFormate } from 'utils/helpers'

const renderEventDetailsRow = (label, value = '') => {
  return (
    !isEmpty(value) && (
      <>
        {label === 'Participant' ? (
          <Col xs={12} className="px-0">
            <div className={`mx-0`}>
              <div>
                <TextWrapper className={`font-size-inherit popover-label`}>{label}:</TextWrapper>
              </div>
              <Box>
                <Stack direction="row" spacing={2}>
                  {value &&
                    value.map((v) => (
                      <Tooltip key={v.id} title={v.NomPreNom}>
                        <Avatar key={v.id} alt={v.NomPreNom} src={v.Picture} />
                      </Tooltip>
                    ))}
                </Stack>
              </Box>
            </div>
          </Col>
        ) : (
          <Col xs={12} className="px-0">
            <div className={`mx-0`}>
              <div>
                <TextWrapper className={`font-size-inherit popover-label`}>{label}:</TextWrapper>
              </div>
              <div>{value}</div>
            </div>
          </Col>
        )}
      </>
    )
  )
}

// Pièces jointes de l'événement. Même présentation que le bloc documents d'une
// tâche (MesTaches/ListOperations) : icône trombone, lien vers le fichier, date
// d'import et taille — pour que l'utilisateur retrouve le même repère visuel
// que le document soit consulté depuis la tâche ou depuis l'agenda.
const renderEventDocuments = (documents, siteUser) => {
  const files = (documents || []).filter((d) => d && d.Lien)
  if (files.length === 0) {
    return null
  }
  return (
    <Col xs={12} className="px-0">
      <div className="mx-0">
        <div>
          <TextWrapper className="font-size-inherit popover-label">
            Documents ({files.length}):
          </TextWrapper>
        </div>
        {files.map((doc) => (
          <div key={doc.id} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <AttachmentIcon sx={{ fontSize: '18px' }} />
            <div>
              <a href={doc.Lien} target="_blank" rel="noopener noreferrer">
                {doc.Nom || doc.Lien.split('/').pop()}
              </a>
              <br />
              <small>
                {convertToUserDateFormate(siteUser?.user, doc.Datedimport)}
                {doc.FileSize ? ` - ${doc.FileSize} Mio` : ''}
              </small>
            </div>
          </div>
        ))}
      </div>
    </Col>
  )
}

const EventPopOverCard = ({
  item: {
    type,
    category,
    title = 'Event',
    code,
    start,
    end,
    location,
    isAllDay,
    eventType,
    region,
    designation,
    pays,
    participants,
    participants_list,
    reschedule_count, // LAP-770 — nb de fois où la date/heure a été déplacée
    documents, // pièces jointes (propres à l'événement ou héritées de la tâche)
  },
  id,
  event_id,
  textClassName,
  isOpen,
  getAllEvents = false,
  removeEventLocally = false,
}) => {
  // Le Context est le canal fiable (react-big-calendar ne forwarde pas nos
  // props/messages custom jusqu'ici). On garde les props en repli.
  const eventActions = useContext(EventActionsContext)
  const doRefetch = eventActions?.getAllEvents || getAllEvents
  const doRemoveLocally = eventActions?.removeEventLocally || removeEventLocally

  const siteUser = useSelector((store) => store.user)
  let time_formate = siteUser.user?.user_parameter?.format_de_lheure

  const [showModal, setShowModal] = useState(false)
  const toggleModal = useCallback(() => {
    setShowModal(!showModal)
  })

  const de = `${start.getDate()} ${getFrench(start.getMonth() + 1)} ${start.getFullYear()} ${moment(
    start.toLocaleTimeString('en-US'),
    'hh:mm:ss a',
  ).format(time_formate === '24h' ? 'HH:mm:ss' : 'hh:mm:ss A')}`
  const a = `${end.getDate()} ${getFrench(end.getMonth() + 1)} ${end.getFullYear()} ${moment(
    end.toLocaleTimeString('en-US'),
    'hh:mm:ss a',
  ).format(time_formate === '24h' ? 'HH:mm:ss' : 'hh:mm:ss A')}`
  return (
    <>
      <PopOver
        id={`${id}-${start.getDate()}-${end.getDate()}`}
        eid={`${event_id}`}
        text={eventType === 'f' ? designation || title : title}
        textClassName={textClassName}
        showLink={false}
        showModal={showModal}
        content={title}
        isOpen={isOpen}
      >
        {eventType === 'a' && (
          <>
            {renderEventDetailsRow('Lieux', location)}
            {renderEventDetailsRow('de', de)}
            {renderEventDetailsRow('a', a)}
            <input type={'checkbox'} checked={isAllDay} readOnly={true} />
            <span> Journee entière</span>
            {participants_list &&
              participants_list.length > 0 &&
              renderEventDetailsRow('Participant', participants_list)}
            {/* {renderEventDetailsRow('Participant', participants)} */}
            {renderEventDetailsRow('Categorie', category)}
            {renderEventDetailsRow('Type', type)}
            {renderEventDocuments(documents, siteUser)}
            {/* LAP-770 — compteur de reports : visible dès qu'il y a eu au
                moins un déplacement de date/heure. Au-delà de 2, le Maître du
                temps le signale comme procrastination. */}
            {Number(reschedule_count) > 0 && (
              <Col xs={12} className="px-0">
                <div className="mx-0">
                  <div>
                    <TextWrapper className="font-size-inherit popover-label">
                      Reporté:
                    </TextWrapper>
                  </div>
                  <div>
                    {reschedule_count} fois
                    {Number(reschedule_count) >= 2 ? ' ⚠️' : ''}
                  </div>
                </div>
              </Col>
            )}

            <Button
              style={{ backgroundColor: '#5D86CC' }}
              size="sm"
              onClick={() => setShowModal(true)}
            >
              {getFrench('edit')}
            </Button>
            <Button
              color="danger"
              size="sm"
              onClick={() => {
                makeDeleteRequest(`/api/time/events/${event_id}`, {}, true).then((res) => {
                  // makeDeleteRequest résout aussi en cas d'échec (catch =>
                  // error.response) : on vérifie donc explicitement le statut.
                  const ok = res && res.status >= 200 && res.status < 300
                  if (!ok) {
                    return
                  }
                  // window.location.reload()
                  // Retrait local immédiat : l'événement disparaît tout de suite
                  // du calendrier. On NE rappelle PAS getAllEvents() ici — juste
                  // après le DELETE, /api/time/events/ peut renvoyer une liste
                  // encore en cache et ré-afficher l'événement supprimé. Le
                  // retrait local suffit (la suppression ne change aucun autre
                  // événement). getAllEvents reste un repli si le retrait local
                  // n'est pas disponible.
                  if (doRemoveLocally) {
                    doRemoveLocally(event_id)
                  } else if (doRefetch) {
                    doRefetch()
                  }
                })
              }}
            >
              {getFrench('delete')}
            </Button>
          </>
        )}
        {eventType === 'f' && (
          <>
            {renderEventDetailsRow('Region', region)}
            {renderEventDetailsRow('Pays', pays)}
            {renderEventDetailsRow('Designation', designation)}
          </>
        )}
      </PopOver>
      {showModal && (
        <FormModal
          isOpen={showModal}
          getAllEvents={doRefetch}
          toggleModal={toggleModal}
          title={'Rendez-vous'}
          editMode={true}
          eventId={event_id}
        />
      )}
    </>
  )
}

export default EventPopOverCard
